import Service from '@ember/service';
import { allSettled, didCancel, Task, task } from 'ember-concurrency';
import { service } from '@ember/service';
import Evented from '@ember/object/evented';
import RSVP from 'rsvp';

import type RouterService from '@ember/routing/router-service';
import { type Transition } from 'admin-panel/utils/type-utils';

export interface ModalContext {
  exitAnimationTask: Task<unknown, never[]>;
}

export default class ModalManagerService extends Service.extend(Evented) {
  @service
  declare router: RouterService;

  private _abortInProgress = false;

  private _boundTransitionHandler:
    | ((transition: Transition) => Promise<void>)
    | null = null;

  private _modals: Set<ModalContext> = new Set();

  get hasOpenModals() {
    return !!this._modals.size;
  }

  register(context: ModalContext) {
    this._modals.add(context);
  }

  unregister(context: ModalContext) {
    this._modals.delete(context);
  }

  async closeOpenModals() {
    try {
      await this.allExitAnimationsTask.perform();
    } catch (error) {
      if (!didCancel(error)) throw error;
    }
  }

  setupTransitionHandler() {
    this._boundTransitionHandler = this.handleTransition.bind(this);
    this.router.on('routeWillChange', this._boundTransitionHandler);
  }

  removeTransitionHandler() {
    if (this._boundTransitionHandler) {
      this.router.off('routeWillChange', this._boundTransitionHandler);
      this._boundTransitionHandler = null;
    }
  }

  async handleTransition(transition: Transition) {
    if (!transition.from) return;

    if (
      transition.from.name === transition.to.name &&
      hasSameRouteParams(transition) &&
      (!isPageEditorRoute(transition.from.name) ||
        hasSameQueryParams(transition))
    ) {
      return;
    }

    if (!this.hasOpenModals || this._abortInProgress) return;

    try {
      this._abortInProgress = true;

      transition.abort();

      if (this.has('transitionAborted')) {
        const deferred = RSVP.defer();

        deferred.reject = deferred.reject.bind(this, 'DEFER REJECTED');

        this.trigger('transitionAborted', deferred);

        await deferred.promise;
      }

      if (!shouldPreserveOpenModals(transition)) {
        await this.closeOpenModals();
      }

      transition.retry();
    } catch (error) {
      // ignore defer reject
      if (error !== 'DEFER REJECTED') throw error;
    } finally {
      this._abortInProgress = false;
    }
  }

  allExitAnimationsTask = task({ drop: true }, async () => {
    await allSettled(
      [...this._modals].map(async (context) => {
        await context.exitAnimationTask.perform();

        this.unregister(context);
      })
    );
  });
}

function isPageEditorRoute(routeName: string) {
  return (
    routeName === 'authenticated.hotel.category-list.page-list.page-edit' ||
    routeName === 'authenticated.hotel.category-list.page-list.page-create'
  );
}

function shouldPreserveOpenModals(transition: Transition) {
  const fromName = transition.from?.name;

  return (
    !!fromName &&
    isPageEditorRoute(fromName) &&
    fromName === transition.to.name &&
    hasSameRouteParams(transition)
  );
}

function hasSameRouteParams(transition: Transition) {
  const fromParams = routeParamsFor(transition.from);
  const toParams = routeParamsFor(transition.to);
  const keys = new Set([...Object.keys(fromParams), ...Object.keys(toParams)]);

  for (const key of keys) {
    if (fromParams[key] !== toParams[key]) {
      return false;
    }
  }

  return true;
}

type RouteInfoWithParams = {
  params?: Record<string, string | undefined>;
  parent?: RouteInfoWithParams | null;
};

function routeParamsFor(
  routeInfo: RouteInfoWithParams | null | undefined
): Record<string, string | undefined> {
  if (!routeInfo) return {};

  return {
    ...routeParamsFor(routeInfo.parent),
    ...(routeInfo.params ?? {}),
  };
}

function hasSameQueryParams(transition: Transition) {
  const fromQueryParams = transition.from?.queryParams || {};
  const toQueryParams = transition.to.queryParams || {};
  const keys = new Set([
    ...Object.keys(fromQueryParams),
    ...Object.keys(toQueryParams),
  ]);

  for (const key of keys) {
    if (fromQueryParams[key] !== toQueryParams[key]) {
      return false;
    }
  }

  return true;
}

declare module '@ember/service' {
  interface Registry {
    'modal-manager': ModalManagerService;
  }
}
